/**
 * Where to buy a bought part.
 *
 * A `buy` type already carries a supplier and their part number, which tell you
 * *what* to order and say nothing about *how*. In practice the answer is a URL
 * somebody found once and then everybody re-finds, so this stores it: open the
 * shelf count, see you are low, click through to the page that sells it.
 *
 * **The one rule with teeth is the scheme check.** These URLs are typed by a
 * person and rendered as links somebody else clicks, which is exactly the shape
 * of a stored cross-site scripting hole: `javascript:` in an `href` runs when
 * clicked, with the session of whoever clicked it. Refusing anything that is not
 * `http`/`https` is what closes it, and it is refused *here* — in the domain,
 * tested — rather than in a component that a later screen might forget to use.
 *
 * This is not the `Vendor` object of §4.6. That arrives at M5 with contacts,
 * part numbers and price breaks, and it will answer "what do we buy from
 * Digi-Key?" — a question these links deliberately do not try to. They are a
 * shortcut to a page, and keeping them that small is what stops them becoming a
 * second, worse vendor model.
 */

export interface PurchaseLink {
  /** What to call it on a button — "Digi-Key", "the supplier's page". */
  readonly label: string
  readonly url: string
}

/** Enough for a supplier, a distributor and a couple of alternates. Past that
 *  it is a vendor list, which is M5's job and a different shape. */
export const MAX_LINKS = 8
const MAX_URL = 2000
const MAX_LABEL = 40

export type LinkRefusal =
  | { readonly ok: true; readonly link: PurchaseLink }
  | {
      readonly ok: false
      readonly reason: 'empty' | 'not_a_url' | 'bad_scheme' | 'too_long' | 'duplicate' | 'too_many'
      readonly detail: string
    }

/**
 * A scheme that is safe to put in an `href` a colleague will click.
 *
 * Allow-list, never a block-list. `javascript:` is the one everybody thinks of,
 * but `data:` carries a whole document and `vbscript:` still runs in places —
 * and the next one has not been invented yet. Naming the two that are fine is a
 * rule that keeps working; naming the bad ones is a rule that ages.
 */
const SAFE_SCHEMES: readonly string[] = ['http:', 'https:']

export function checkPurchaseLink(
  raw: { readonly label?: string; readonly url: string },
  existing: readonly PurchaseLink[] = [],
): LinkRefusal {
  const url = raw.url.trim()
  if (url === '') return { ok: false, reason: 'empty', detail: 'paste a link first' }
  if (url.length > MAX_URL) {
    return { ok: false, reason: 'too_long', detail: 'that link is implausibly long' }
  }
  if (existing.length >= MAX_LINKS) {
    return {
      ok: false,
      reason: 'too_many',
      detail: `${MAX_LINKS} links is enough for a supplier and its alternates — a longer list is a vendor list, which is a different thing`,
    }
  }

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return {
      ok: false,
      reason: 'not_a_url',
      detail: 'that is not a web address — it needs to start with https://',
    }
  }
  if (!SAFE_SCHEMES.includes(parsed.protocol)) {
    return {
      ok: false,
      reason: 'bad_scheme',
      detail: `only http and https links can be saved — "${parsed.protocol}" is not something it is safe to hand somebody to click`,
    }
  }
  if (existing.some((l) => l.url === parsed.href)) {
    return { ok: false, reason: 'duplicate', detail: 'that link is already here' }
  }

  return {
    ok: true,
    link: { label: labelFor(raw.label, parsed), url: parsed.href },
  }
}

/**
 * What to write on the button.
 *
 * Blank falls back to the host, minus `www.`, because "digikey.com" is what
 * somebody would have typed anyway and asking for it twice is friction for no
 * information.
 */
function labelFor(given: string | undefined, url: URL): string {
  const label = (given ?? '').trim().slice(0, MAX_LABEL)
  if (label !== '') return label
  return url.hostname.replace(/^www\./, '')
}

/**
 * Filter a stored list down to what is still safe to render.
 *
 * Rows written before a rule existed, or by a route that skipped it, must not
 * become clickable just because they are in the database. Reads go through this
 * so that "validated on the way in" is not the only thing standing between a
 * stored `javascript:` and a click.
 */
export function safeLinks(links: readonly PurchaseLink[]): readonly PurchaseLink[] {
  return links.filter((l) => {
    try {
      return SAFE_SCHEMES.includes(new URL(l.url).protocol)
    } catch {
      return false
    }
  })
}

/** The host, for a compact hint under a button — "buy at digikey.com". */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}
